import {useMutation, useQueryClient} from '@github-ui/react-query'
import {useAddBanner, useFocusBanner} from '@github-ui/banner-stack'
import type {QueryKey} from '@github-ui/react-query'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import type {WorkflowRunsRoutePayload} from '@github-ui/dotcom-schema/types/actions/v1/workflow_runs_route'
import {isActionsWorkflowsRouteQueryKey} from './use-workflow-disable'
import {updateWorkflowStateInPaginationCache} from './use-workflow-pagination'

type DisabledWorkflowBannerCacheData = {
  payload: WorkflowRunsRoutePayload
}

type WorkflowEnableResponse = {
  message: string
}

/**
 * Enables a single workflow (e.g. via the "Enable workflow" / "Continue running
 * workflow" action on `DisabledWorkflowBanner`).
 */
export function useWorkflowEnable({
  queryKey,
  path,
  owner,
  repo,
  workflowId,
}: {
  queryKey: QueryKey
  path: string
  owner: string
  repo: string
  workflowId: bigint
}) {
  const queryClient = useQueryClient()
  const addBanner = useAddBanner()
  const focusBanner = useFocusBanner()

  return useMutation<WorkflowEnableResponse, Error, void>({
    mutationFn: async () => {
      const response = await verifiedFetchJSON(path, {
        method: 'POST',
      })
      const data: WorkflowEnableResponse = await response.json()
      if (!response.ok) {
        throw new Error(data.message)
      }
      return data
    },
    onSuccess: data => {
      const message = data.message ?? 'Workflow enabled successfully.'
      const id = addBanner({
        title: message,
        variant: 'success',
        dismissible: true,
      })

      requestAnimationFrame(() => focusBanner(id))
    },
    onError: error => {
      const id = addBanner({
        title: error.message ?? 'There was an error enabling this workflow.',
        variant: 'critical',
        dismissible: true,
      })
      requestAnimationFrame(() => focusBanner(id))
    },
    onSettled: async (_, error) => {
      if (error) return
      queryClient.setQueryData<DisabledWorkflowBannerCacheData>(queryKey, oldData => {
        if (!oldData) return oldData
        return {
          ...oldData,
          payload: {
            ...oldData.payload,
            disabledWorkflowBanner: undefined,
          },
        }
      })
      updateWorkflowStateInPaginationCache({
        queryClient,
        owner,
        repo,
        workflowId,
        state: 'active',
      })
      await queryClient.invalidateQueries({
        predicate: query => isActionsWorkflowsRouteQueryKey(query.queryKey, queryKey[0]),
      })
    },
  })
}
